/**
 * Boot prefetch for MFE bundles.
 *
 * A shim asks for its MFE to be warmed from its own module scope, as it always
 * has, so only the MFEs whose shim was actually evaluated get fetched. The one
 * thing that can delay a request is a cluster where the manifest may replace
 * the delivery payload: warming before that decision would fetch the
 * bootstrap's build and then the manifest's. Clusters with no manifest URL
 * warm immediately — identical to the behaviour before the registry existed.
 */
import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { Microfrontend } from '@thoughtspot/microfrontends';
import { initMicroFrontend } from './microfrontends.util';

declare const window: any;

/** True once the registry has published its decision. */
let decided = false;
const queued = new Set<Microfrontend>();

/**
 * Synchronous and independent of LaunchDarkly: with no injected manifest URL
 * (and no dev override) no manifest can apply to this page load whatever
 * `enableMfeManifest` says, so there is nothing to wait for. Keeps flag-off
 * clusters on the original timing instead of making them pay for a decision
 * that cannot change their delivery.
 */
const canManifestApply = (): boolean =>
    typeof window?.blink?.mfeManifestUrl === 'string' ||
    getQueryParamsAndQueryString().queryParams?.mfeCdnEnv !== undefined;

/** initMicroFrontend logs and evicts its own failures; the render path retries. */
const warm = (mfe: Microfrontend): void => {
    initMicroFrontend(mfe).catch((): void => undefined);
};

/** Called from a shim's module scope. */
export const prefetchMicroFrontend = (mfe: Microfrontend): void => {
    if (decided || !canManifestApply()) {
        warm(mfe);
        return;
    }
    queued.add(mfe);
};

/** Bootstrap, immediately after initMfeRegistry() has published its decision. */
export const startMfePrefetch = (): void => {
    decided = true;
    queued.forEach(warm);
    queued.clear();
};

/** Test-only. */
export const resetMfePrefetchForTests = (): void => {
    decided = false;
    queued.clear();
};
